import React, { ChangeEvent, useState } from "react";
import Input from "@/components/Input/Input";
import { Button, Modal } from "@mui/material";
import styles from "./AddItemModal.module.scss";
import addPngIcon from "@/assets/icons/image-upload.svg";
import { getButtonLabel } from "../../helperForStoryTeller";
import { truncateDescription } from "@/helpers/truncateDescr";
import Textarea from "@/components/Textarea/Textarea";

type Props = {
  type: string;
  isOpenAddItemModal: boolean;
  setIsOpenAddItemModal: (isOpenAddItemModal: boolean) => void;
  onAddCharacter: () => void;
  optionName: string;
  setOptionName: (name: string) => void;
  setSelectedFile: (file: File | null) => void;
  optionDescription: string;
  setOptionDescription: (description: string) => void;
};

export const AddItemModal = ({
  type,
  isOpenAddItemModal,
  setIsOpenAddItemModal,
  onAddCharacter,
  optionName,
  setOptionName,
  setSelectedFile,
  optionDescription,
  setOptionDescription,
}: Props) => {
  const [fileName, setFileName] = useState<string>("");
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [error, setError] = useState<{
    name: boolean;
    description: boolean;
    file: boolean;
  }>({
    name: false,
    description: false,
    file: false,
  });

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFileName(file!.name);
    setSelectedFile(file);
    setImagePreview(URL.createObjectURL(file!));
    setError((prev) => ({ ...prev, file: false }));
  };

  const handleSave = () => {
    const isNameValid = !!optionName.trim();
    const isFileValid = !!fileName;

    const requiredDescr = ["Hero", "Villain", "Special item"].includes(type);
    const isDescriptionValid = requiredDescr
      ? !!optionDescription.trim()
      : true;

    if (!isNameValid || !isDescriptionValid || !isFileValid) {
      setError({
        name: !isNameValid,
        description: requiredDescr ? !isDescriptionValid : false,
        file: !isFileValid,
      });
      return;
    }

    setError({ name: false, description: false, file: false });
    onAddCharacter();
  };

  return (
    <Modal
      aria-labelledby="modal-adding-item-to-content"
      aria-describedby="Add an item to the system. Enter the required data and click save"
      open={isOpenAddItemModal}
      onClose={() => setIsOpenAddItemModal(false)}
    >
      <div className={styles.modalContainer}>
        <h3 className={styles.modalTitle}>{getButtonLabel(type)}</h3>
        <Input
          label="Name"
          value={optionName}
          onChange={(e) => {
            setOptionName(e.target.value);
            setError((prev) => ({ ...prev, name: false }));
          }}
          hasError={error.name}
        />
        {(type === "Hero" || type === "Villain" || type === "Special item") && (
          <Textarea
            label="Description"
            value={optionDescription}
            onChange={(e) => {
              setOptionDescription(e.target.value);
              setError((prev) => ({ ...prev, description: false }));
            }}
            hasError={error.description}
          />
        )}
        <label className={styles.modalAddItem} htmlFor="img" id="dropcontainer">
          <img className={styles.modalImg} src={addPngIcon} alt="img" />
          <input
            id="img"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            required
          />

          <span className={styles.modalPrimaryText}>
            {fileName
              ? truncateDescription(fileName, 15)
              : "or drag and drop the file here (*.png)"}
          </span>
          {error.file && <p className={styles.errorText}>Image is required</p>}
        </label>

        {imagePreview && (
          <img
            className={styles.thumbnailPreview}
            src={imagePreview}
            alt="Thumbnail Preview"
            width={100}
            height={100}
          />
        )}
        <div className={styles.btns}>
          <Button
            sx={{
              textTransform: "capitalize",
              height: "44px",
              width: "130px",
              backgroundColor: "#008000",
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "#008000",
              },
            }}
            variant="contained"
            onClick={handleSave}
          >
            Save
          </Button>

          <Button
            variant="outlined"
            sx={{
              textTransform: "capitalize",
              height: "44px",
              width: "130px",
              border: "1px solid #5570F1",
              color: "#5570F1",
              borderRadius: "12px",
            }}
            onClick={() => setIsOpenAddItemModal(false)}
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

